import {
  DEFAULT_FEED_COUNT,
  MAX_FEED_COLUMNS,
  MAX_FEED_COUNT,
  MAX_FEED_LABEL_LENGTH,
  type FeedSource,
  type ProductFeed,
} from '@nopain-newsletter/core/blocks';
import type { Block } from '../lib/blocks';
import { useCatalog, useRefreshCatalog } from '../lib/useCatalog';
import { ApiError } from '../types/api';
import { SkeletonRows } from './ui/Skeleton';

type FeedBlock = Extract<Block, { type: 'products' }>;

const SOURCES: ReadonlyArray<{ value: FeedSource; label: string }> = [
  { value: 'newest', label: 'Newest products' },
  { value: 'best', label: 'Best sellers' },
  { value: 'picked', label: 'Hand-picked' },
];

const LANGUAGES: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'German' },
  { value: 'da', label: 'Danish' },
  { value: 'sv', label: 'Swedish' },
  { value: 'nl', label: 'Dutch' },
  { value: 'fr', label: 'French' },
  { value: 'es', label: 'Spanish' },
  { value: 'it', label: 'Italian' },
];

function numbers(max: number): number[] {
  return Array.from({ length: max }, (_, i) => i + 1);
}

/**
 * Settings of a "Product feed" block (K4): which products, how many, how they
 * are laid out. The products are filled in from the shop catalogue when the
 * mail is prepared, so every recipient sees the same ones. The catalogue is
 * refreshed by hand from the shop, never on its own.
 */
export function ProductFeedFields({
  block,
  onChange,
  idPrefix,
}: {
  block: FeedBlock;
  onChange: (next: Block) => void;
  idPrefix: string;
}) {
  const { products, refreshedAt, isLoading, isError } = useCatalog();
  const refresh = useRefreshCatalog();
  const feed = block.feed;
  const id = (f: string) => `${idPrefix}-${f}`;
  const set = (patch: Partial<ProductFeed>) => onChange({ ...block, feed: { ...feed, ...patch } });

  const setSource = (source: FeedSource) => {
    if (source === 'picked') {
      set({ source, ids: feed.ids, count: Math.max(1, feed.ids.length) });
    } else {
      set({ source, ids: [], count: feed.source === 'picked' ? DEFAULT_FEED_COUNT : feed.count });
    }
  };
  const toggle = (productId: string) => {
    const ids = feed.ids.includes(productId)
      ? feed.ids.filter((x) => x !== productId)
      : [...feed.ids, productId].slice(0, MAX_FEED_COUNT);
    set({ ids, count: Math.max(1, ids.length) });
  };

  const refreshError =
    refresh.error instanceof ApiError
      ? (refresh.error.body.message ?? 'The shop could not be reached.')
      : refresh.error
        ? 'The shop could not be reached.'
        : null;
  const missing = feed.ids.filter((x) => !isLoading && !products.some((p) => p.id === x));

  return (
    <>
      <div className="pf-row2">
        <div className="pf-field">
          <label className="pf-flabel" htmlFor={id('source')}>
            Products
          </label>
          <select
            id={id('source')}
            value={feed.source}
            onChange={(e) => setSource(e.target.value as FeedSource)}
          >
            {SOURCES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
        {feed.source !== 'picked' ? (
          <div className="pf-field">
            <label className="pf-flabel" htmlFor={id('count')}>
              How many
            </label>
            <select
              id={id('count')}
              value={feed.count}
              onChange={(e) => set({ count: Number(e.target.value) })}
            >
              {numbers(MAX_FEED_COUNT).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>
      <div className="pf-row2">
        <div className="pf-field">
          <label className="pf-flabel" htmlFor={id('columns')}>
            Products per row
          </label>
          <select
            id={id('columns')}
            value={feed.columns}
            onChange={(e) => set({ columns: Number(e.target.value) })}
          >
            {numbers(MAX_FEED_COLUMNS).map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="pf-field">
          <label className="pf-flabel" htmlFor={id('lang')}>
            Price format
          </label>
          <select id={id('lang')} value={feed.lang} onChange={(e) => set({ lang: e.target.value })}>
            {LANGUAGES.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="pf-field">
        <label className="pf-flabel" htmlFor={id('label')}>
          Button text
        </label>
        <input
          id={id('label')}
          type="text"
          value={feed.label}
          maxLength={MAX_FEED_LABEL_LENGTH}
          onChange={(e) => set({ label: e.target.value })}
        />
      </div>

      {feed.source === 'picked' ? (
        <fieldset className="pf-field">
          <legend className="pf-flabel">Choose up to {MAX_FEED_COUNT} products</legend>
          {products.length === 0 ? (
            isLoading ? (
              <SkeletonRows
                label="Loading the product list…"
                rows={5}
                rowClassName="h-6"
                className="flex flex-col gap-1"
              />
            ) : (
              <p className="pf-hint">No products yet. Refresh from the shop.</p>
            )
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {products.map((p) => (
                <li key={p.id}>
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      checked={feed.ids.includes(p.id)}
                      disabled={!feed.ids.includes(p.id) && feed.ids.length >= MAX_FEED_COUNT}
                      onChange={() => toggle(p.id)}
                    />
                    {p.name}
                    {p.stockStatus === 'outofstock' ? ' (out of stock, left out)' : ''}
                  </label>
                </li>
              ))}
            </ul>
          )}
          {feed.ids.length === 0 ? (
            <p className="pf-hint pf-hint-warn">Pick at least one product.</p>
          ) : null}
          {missing.length > 0 ? (
            <p className="pf-hint pf-hint-warn">
              {missing.length === 1
                ? 'One picked product is no longer in the list and will be left out.'
                : `${missing.length} picked products are no longer in the list and will be left out.`}
            </p>
          ) : null}
        </fieldset>
      ) : null}

      <div className="pf-field">
        <button
          type="button"
          className="pf-btn pf-btn-sm pf-btn-ghost"
          disabled={refresh.isPending}
          onClick={() => refresh.mutate()}
        >
          {refresh.isPending ? 'Refreshing…' : 'Refresh from shop'}
        </button>
        <p className="pf-hint">
          {refreshedAt
            ? `Product list updated ${new Date(refreshedAt).toLocaleString('en-GB')}.`
            : 'The product list has not been loaded from the shop yet.'}{' '}
          Prices and stock are frozen into a campaign when it is prepared. Products that are out of
          stock are left out.
        </p>
        {isError ? (
          <p className="pf-hint pf-hint-warn">The product list could not be loaded.</p>
        ) : null}
        {refreshError ? <p className="pf-hint pf-hint-warn">{refreshError}</p> : null}
      </div>
    </>
  );
}
